import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import HrHeader from '../HrHeader';
import HrSideBar from '../HrSideBar';
import Table from '../../../../src/components/tableComponents/Table';
import Dropdown from '../../../components/dropdown/DropDown';
import { fetchCollaborationImage, deleteCollaborator } from '../../../redux/actions/action';
import AddCollaboratorForm from '../Collaboration/AddCollaboratorForm';
import ViewCollaborator from './ViewCollaborator';
import DeleteCollaborator from './DeleteCollaborator';
import EditCollaborator from './EditCollaborator';
import instance from '../../../api/axios';

const Collaborationbackend = () => {
  const [showCollaborationForm, setShowCollaborationForm] = useState(false);
  const [showViewCollaboration, setShowViewCollaboration] = useState(false);
  const [showEditCollaboration, setShowEditCollaboration] = useState(false);
  const [showDeleteCollaboration, setShowDeleteCollaboration] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [remainders, setRemainders] = useState([]);

  const dispatch = useDispatch();
  const collaborations = useSelector(
    (state) => state.fetchcollaborationimage?.imageData || [],
    shallowEqual
  );

  // Fetch reminders from backend
  const fetchRemainders = useCallback(async () => {
    try {
      const res = await instance.get('/api/collaborations/collaboration/remainders');
      setRemainders(res.data.data);
    } catch (err) {
      console.error("Error fetching remainders:", err);
    }
  }, []);


  const loadCollaboration = useCallback(async () => {
    try {
      await dispatch(fetchCollaborationImage());
    } catch (error) {
      console.error('Error fetching collaborations:', error);
    }
  }, [dispatch]);


  useEffect(() => {
    loadCollaboration();
    fetchRemainders();
  }, [dispatch, fetchRemainders, loadCollaboration]);



  const formattedData = useMemo(() => {
    return collaborations.map((collab, index) => {
      const reminder = remainders.find(r => r.projectNo === collab.projectNo);
      return {
        ...collab,
        slno: index + 1,
        status: 'Active',
        renewalStatus: '',
        daysRemaining: reminder?.daysRemaining ?? collab.daysRemaining ?? null,
        projectNo: collab.projectNo
      };
    });
  }, [collaborations, remainders]);

  

  useEffect(() => {
    setTableData(formattedData);
  }, [formattedData]);

  const openCollaborationForm = () => setShowCollaborationForm(true);
  const closeCollaborationForm = () => setShowCollaborationForm(false);
  const openView = (event) => { setSelectedEvent(event); setShowViewCollaboration(true); };
  const openEdit = (event) => { setSelectedEvent(event); setShowEditCollaboration(true); };
  const openDelete = (event) => {
    if (event.renewalStatus === 'No') {
      setSelectedEvent(event);
      setShowDeleteCollaboration(true);
    } else {
      alert("Can't delete, Renewal Status is not 'No'");
    }
  };

  const handleSave = async () => { await loadCollaboration(); closeCollaborationForm(); };

  const handleRenewalStatusUpdate = useCallback(
    (rowIndex, value) => {
      const updatedData = [...tableData];
      updatedData[rowIndex] = {
        ...updatedData[rowIndex],
        renewalStatus: value,
        status: value === 'No' ? 'Remove' : 'Active',
      };
      setTableData(updatedData);
    },
    [tableData]
  );

  const handleDeleteRow = (projectNo) => {
    const updatedData = tableData.filter((item) => item.projectNo !== projectNo);
    setTableData(updatedData);
  };

  const handleUpdateCollaboration = async (updatedData) => {
    try {
      const formDataToSend = new FormData();
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] !== null && updatedData[key] !== undefined) {
          formDataToSend.append(key, updatedData[key]);
        }
      });

      const response = await instance.put(`/api/CollaborationImgAWS/${updatedData.projectNo}`, formDataToSend);

      const updatedTableData = tableData.map((item) =>
        item.projectNo === updatedData.projectNo ? { ...item, ...response.data } : item
      );
      setTableData(updatedTableData);
      await dispatch(fetchCollaborationImage());

      setShowEditCollaboration(false);
    } catch (error) {
      console.error("Error updating collaboration:", error);
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'S.No', accessor: 'slno' },
      {
        Header: 'Collaborators Logo',
        accessor: 'logo',
        Cell: ({ row }) =>
          row.original.status !== 'Remove' ? (
            <div className="flex flex-col items-center">
              <img src={row.original.image_url} alt="Logo" width={50} />
              <p className="text-sm mt-1 text-center">{row.original.organizationName}</p>
              {row.original.daysRemaining !== null && (
                <span className={`mt-1 text-xs px-2 py-1 rounded ${row.original.daysRemaining < 0 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                  {row.original.daysRemaining < 0
                    ? 'Expired'
                    : `⏳ Renewal in ${row.original.daysRemaining} days`}
                </span>
              )}
            </div>
          ) : (
            <span className="text-red-500 font-semibold">Removed</span>
          ),
      },
      {
        Header: 'Duration of Collaboration',
        accessor: 'duration',
        Cell: ({ row }) => {
          const start = new Date(row.original.startdate).toLocaleDateString();
          const end = new Date(row.original.enddate).toLocaleDateString();
          return <div>{start} - {end}</div>;
        },
      },
      {
        Header: 'Renewal of Collaboration',
        accessor: 'renewalStatus',
        Cell: ({ row }) => (
          <select
            value={row.original.renewalStatus}
            onChange={(e) => handleRenewalStatusUpdate(row.index, e.target.value)}
            className="block w-full p-2 border border-gray-300 rounded-md text-black"
          >
            <option value="" disabled>Select Yes/No</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        ),
      },
      { Header: 'Status', accessor: 'status', Cell: ({ row }) => <span>{row.original.status}</span> },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <Dropdown
            options={['View', 'Edit', 'Delete']}
            onSelect={(option) => {
              if (option === 'View') openView(row.original);
              if (option === 'Edit') openEdit(row.original);
              if (option === 'Delete') openDelete(row.original);
            }}
          />
        ),
      },
    ],
    [handleRenewalStatusUpdate]
  );

  return (
    <div className="flex flex-row w-full h-screen bg-[#090119] overflow-hidden">
      <HrHeader dashboardName="Collaboration Management" />
      <HrSideBar />
      <div className="md:flex-1 md:ml-8 px-4 pt-24 pl-60">
        <div className="text-white">
          <div className="mb-2 pt-4 flex justify-end items-center pr-8">
            <button
              onClick={openCollaborationForm}
              className="bg-color hover:bg-blue-700 text-white font-bold py-2 px-4 rounded lg:w-[60px] lg:mr-[-30px] lg:absolute top-[130px] xl:w-[150px] xl:mt-2 xl:mr-40"
            >
              Add
            </button>
          </div>
          <Table columns={columns} data={tableData} className="w-full md:w-3/4 lg:w-2/3" />
        </div>
      </div>

      {showCollaborationForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <AddCollaboratorForm onClose={closeCollaborationForm} onSave={handleSave} />
        </div>
      )}

      {showViewCollaboration && (
        <ViewCollaborator event={selectedEvent} onClose={() => setShowViewCollaboration(false)} />
      )}

      {showEditCollaboration && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <EditCollaborator
            event={selectedEvent}
            onClose={() => setShowEditCollaboration(false)}
            onUpdate={handleUpdateCollaboration}
            disableEndDate={selectedEvent?.renewalStatus !== 'Yes'}
          />
        </div>
      )}

      {showDeleteCollaboration && (
        <DeleteCollaborator
          event={selectedEvent}
          onClose={() => setShowDeleteCollaboration(false)}
          onDelete={async () => {
            await dispatch(deleteCollaborator(selectedEvent.projectNo));
            handleDeleteRow(selectedEvent.projectNo);
            setShowDeleteCollaboration(false);
          }}
        />
      )}
    </div>
  );
};

export default Collaborationbackend;
